// React core hooks

import type { AxiosError } from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
// Router
import { useNavigate } from 'react-router-dom';
import Logo from '@/assets/smartcity/logo.svg';
// Modal components
import ModalForgotPassword from '@/domains/auth/components/ModalForgotPassword/ModalForgotPassword';
// Custom hooks
import useAuth from '@/domains/auth/hooks/useAuth';
// CSS Module for styling
import classes from '@/domains/auth/pages/SignIn/SignIn.module.css';
// Types
import type { OptionItem, SignInRequest } from '@/domains/auth/types/auth.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import { Button } from '@/shared/components/ui/Button';
import CheckBox from '@/shared/components/ui/CheckBox/CheckBox';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
// UI Components
import GeneralInput, {
  type GeneralInputRef,
} from '@/shared/components/ui/GeneralInput/GeneralInput';
import PasswordInput from '@/shared/components/ui/PasswordInput/PasswordInput';
import type { PasswordInputRef } from '@/shared/components/ui/PasswordInput/PasswordInput.types';
import { URLS } from '@/shared/config/urls';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
// Utilities
import Common from '@/shared/utils/standard';

const APK_DOWNLOAD_URL = `${URLS.BACK_DSH}/app-version/download/latest`;

function SignIn() {
  const [rememberMe, setRememberMe] = useState(false);
  const { preferenceData } = usePreferenceContext();
  const navigate = useNavigate();
  const idInputRef = useRef<GeneralInputRef>(null);
  const [optionParams] = useState<string>('upper-code=ORG');
  const pwInputRef = useRef<PasswordInputRef>(null);
  const [accountInfo, setAccountInfo] = useState<SignInRequest>({
    identifier: '',
    password: '',
    remember_me: true,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { handleLogin, getIsLogin, resolveLandingRoute } = useAuth({
    onLoginFail: (err) => {
      console.log(err);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // new NoticeMessage(err?.message);
      const axiosErr = err as AxiosError<{ message: string }>;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(axiosErr?.response?.data?.message);
    },
  });

  const handleOnKeyID = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const keyInput = event.key;
    const target = event.target as HTMLInputElement;
    const idValue = target.value;
    if (keyInput === 'Enter') {
      if (idValue === '') {
        return;
      }

      if (pwInputRef.current && !accountInfo.password) {
        pwInputRef.current.focus();
        return;
      }
      onLogin();
    }
  };

  const handleOnKeyPW = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const keyInput = event.key;
    const target = event.target as HTMLInputElement;
    const pwValue = target.value;
    if (keyInput === 'Enter') {
      if (idInputRef.current && !accountInfo.identifier) {
        idInputRef.current.focus();
      }

      if (pwValue === '') {
        return;
      }

      onLogin();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: getIsLogin should only run once on mount to check initial login state and focus input
  useEffect(() => {
    if (idInputRef.current) {
      idInputRef.current.focus();
    }
    const isLogin = getIsLogin();

    if (isLogin) {
      resolveLandingRoute().then(navigate);
    }
  }, [navigate]);

  const onLogin = () => {
    const hashedPassword = Common.SHA256(accountInfo.password.trim());
    const loginPayload = {
      ...accountInfo,
      password: hashedPassword,
    };
    console.log(loginPayload);

    handleLogin(loginPayload);
  };

  const { commonListData } = useCommonCodes({ optionParams });

  const optionORG: OptionItem[] = commonListData?.ORG
    ? [
        { value: '', label: '전체', code: 'All' },
        ...commonListData.ORG.code
          .filter((code): code is string => code !== null)
          .map((code: string, index: number) => ({
            value: code,
            label: commonListData.ORG?.name[index] ?? '',
            code: code,
          })),
      ]
    : [];

  const loginBg = preferenceData?.find(
    (item) => item.preference_key === 'login_background'
  )?.preference_value;
  const loginLogo = preferenceData?.find(
    (item) => item.preference_key === 'login_logo'
  )?.preference_value;

  const whatsappMessage =
    "Hello Admin, I'm having trouble logging in to Grobogan Smart City. Could you please assist me with this issue? Thank you.\n\n" +
    'Halo Admin, saya mengalami kendala saat login ke Grobogan Smart City. Bisakah Anda membantu saya mengatasi masalah ini? Terima kasih.';

  return (
    <section className="login-wrap relative bg-[linear-gradient(180deg,_#FFFFFF_0%,_#007AFF_250.56%)] w-full h-screen !flex flex-col items-center justify-center ">
      <div className="login-wrap-screen grid grid-cols-[1fr_auto_1fr] p-10 gap-10 items-center bg-db-wrap rounded-[24px]">
        <div className="w-full  flex flex-col  gap-6   ">
          <img src={Logo} alt="Logo" className="w-fit" />
          <div className="flex flex-col gap-2">
            <span className="h4medium">Login</span>
            <span>Enter your username and password to continue.</span>
          </div>

          <div className="w-full flex flex-col gap-3">
            <div className="flex flex-col w-full gap-2">
              <span className="title2 text-db-text-grey">Username / Email</span>
              <GeneralInput
                ref={idInputRef}
                customInput={classes._inputUser}
                placeholder="Enter your username or email"
                value={accountInfo.identifier.trim()}
                type={'text'}
                onKeyUp={handleOnKeyID}
                maxLength={20}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAccountInfo((curInfo) => ({
                    ...curInfo,
                    identifier: event.target.value.trim(),
                  }))
                }
              />
            </div>

            <div className="flex flex-col w-full gap-2">
              <span className="title2 text-db-text-grey">Password</span>
              <PasswordInput
                ref={pwInputRef}
                customInput={classes._inputPassword}
                placeholder="Enter your password"
                onKeyUp={handleOnKeyPW}
                maxLength={20}
                value={accountInfo.password.trim()}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAccountInfo((curInfo) => ({
                    ...curInfo,
                    password: event.target.value.trim(),
                  }))
                }
              />
            </div>
          </div>

          <div className="w-full flex flex-col gap-4 ">
            {/* <Button
              variant="ghost"
              type="button"
              className="_modalForgot text-start p-0 h-auto hover:bg-transparent"
              id="modalForgot"
              onClick={() => setIsModalOpen(true)}
            >
              <span>비밀번호를 잊으셨나요?</span>
            </Button> */}
            <Button
              label="Login"
              // disabled={true}
              className="flex-1 btn-fill-blue capitalize text-[20px] disabled:pointer-events-auto disabled:!cursor-not-allowed"
              onClick={onLogin}
            />

            <div className="flex flex-col gap-0.5 text-center text-db-text-grey">
              <span>Having Trouble logging in?</span>
              <span>
                Please{' '}
                <a
                  href={`https://wa.me/6282298126471?text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline"
                >
                  Contact Admin
                </a>{' '}
                for further assistance.
              </span>
            </div>
          </div>
        </div>
        <div className="divider h-full w-[1px] bg-[#e0e0e0]"></div>
        <div className="flex flex-col items-center justify-between w-full  gap-6  ">
          <QRCodeSVG value={APK_DOWNLOAD_URL} size={200} />
          <div className="flex flex-col text-center gap-2">
            <span className="h4medium">Download App via QR Code</span>
            <span className="title2 text-db-text-grey">Scan QR code above to install</span>
          </div>
        </div>
      </div>

      <ModalForgotPassword
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        optionsSelect={optionORG}
      />
    </section>
  );
}

export default SignIn;
